import * as React from "react";
import { Box, Typography, Stack, Paper, Button } from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";

type Choice = "stay" | "leave";

export default function AwareStayOrLeave(props: {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
}) {
  const {
    handlePageChange,
    nextPage = "AwareBaselineScoreScreen",
    backPage = "AwareCrisisVideoScreen",
  } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";

  async function saveDecision(choice: Choice) {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) {
      console.error("Missing employee/workshop");
      return;
    }

    const { error } = await supabase
      .from("workshop_participant_decisions")
      .upsert({
        workshop_id: workshop.id,
        employee_id: emp.id,
        stay_or_leave: choice,
        resilience_type: null,
      });

    if (error) {
      console.error("SAVE ERROR:", error);
    }
  }

  async function handleChoice(choice: Choice) {
    await saveDecision(choice);
    handlePageChange(nextPage);
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(rgba(8,8,8,0.78), rgba(8,8,8,0.84)),
          url("/scenarioBgs/leaveOrstay.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={4.5} alignItems="center">
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.35rem" },
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.25,
            }}
          >
            {isDE ? "Bleiben oder Gehen?" : "Stay or Leave?"}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.84)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              fontWeight: 500,
              lineHeight: 1.65,
              maxWidth: 650,
            }}
          >
            {isDE
              ? "Stell dir vor, eine länger andauernde Krise betrifft deine Region. Nach allem, was du gerade eingeschätzt hast: Was wäre dein erster Impuls?"
              : "Imagine a prolonged crisis affecting your region. Based on everything you have just considered, what would your first instinct be?"}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ width: "100%", justifyContent: "center" }}
          >
            <Button
              variant="outlined"
              onClick={() => handleChoice("stay")}
              sx={{
                flex: 1,
                minHeight: 150,
                borderRadius: 4,
                px: 3,
                py: 2.5,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.35)",
                textTransform: "none",
                background: "rgba(255,255,255,0.04)",
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.08)",
                },
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 900 }}>
                  {isDE ? "In Deutschland bleiben" : "Stay in Germany"}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.45 }}>
                  {isDE
                    ? "Mein Fokus wäre, zu bleiben, meinen Haushalt zu stabilisieren und mich an die Lage anzupassen."
                    : "My focus would be to remain, stabilize my household and adapt to the situation."}
                </Typography>
              </Stack>
            </Button>

            <Button
              variant="contained"
              onClick={() => handleChoice("leave")}
              sx={{
                flex: 1,
                minHeight: 150,
                borderRadius: 4,
                px: 3,
                py: 2.5,
                textTransform: "none",
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #a10c1c, #bf2436)",
                },
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 900 }}>
                  {isDE ? "Das Land verlassen" : "Leave the country"}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.45 }}>
                  {isDE
                    ? "Mein Fokus wäre, in ein anderes Land zu gehen, wenn es mir sicherer erscheint."
                    : "My focus would be to relocate to another country if it seemed safer."}
                </Typography>
              </Stack>
            </Button>
          </Stack>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.62)",
              fontSize: "0.95rem",
              maxWidth: 620,
              lineHeight: 1.55,
            }}
          >
            {isDE
              ? "Es gibt hier keine richtige oder falsche Antwort. Die Entscheidung zeigt nur deinen aktuellen Denkstand vor dem praktischen Teil des Labs."
              : "There is no right or wrong answer here. Your choice simply reflects your current thinking before the practical part of the lab."}
          </Typography>

          <Button
            variant="outlined"
            onClick={() => handlePageChange(backPage)}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 900,
              px: 4,
              py: 1.25,
              borderColor: "rgba(255,255,255,0.4)",
              color: "#fff",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            {isDE ? "Zurück" : "Back"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
